export type UserRole = "admin" | "viewer";

export interface AuthenticatedSession {
  readonly uid: string;
  readonly role: UserRole;
  readonly provider: "google.com" | "password";
  readonly secondFactor: "totp";
}

export type PrimarySignInResult =
  | { readonly kind: "authenticated"; readonly session: AuthenticatedSession }
  | { readonly kind: "mfa-challenge" }
  | {
      readonly kind: "mfa-enrollment";
      readonly accountLabel: string;
      readonly secretKey: string;
      readonly setupUrl: string;
    };

export type RestoredAuthState =
  | { readonly kind: "signed-out" }
  | { readonly kind: "authenticated"; readonly session: AuthenticatedSession };

export interface AuthGateway {
  restore(): Promise<RestoredAuthState>;
  signInWithPassword(
    email: string,
    password: string,
  ): Promise<PrimarySignInResult>;
  signInWithGoogle(): Promise<PrimarySignInResult>;
  completeMfaChallenge(code: string): Promise<AuthenticatedSession>;
  completeMfaEnrollment(code: string): Promise<void>;
  getAccessToken(forceRefresh?: boolean): Promise<string>;
  cancelMfa(): Promise<void>;
  signOut(): Promise<void>;
}

export type SafeAuthErrorCode =
  | "invalid-credentials"
  | "account-disabled"
  | "account-not-provisioned"
  | "access-denied"
  | "google-sign-in-unavailable"
  | "invalid-mfa-code"
  | "network-unavailable"
  | "popup-closed"
  | "session-expired"
  | "unexpected-error";

export class SafeAuthError extends Error {
  readonly code: SafeAuthErrorCode;

  constructor(code: SafeAuthErrorCode) {
    super(code);
    this.name = "SafeAuthError";
    this.code = code;
  }
}
